// ============================================================
// TaskFlow OS - Sidebar Navigation
// Liquid Glass Design
// ============================================================

import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuthStore } from '../../stores/authStore';
import {
  IconTaskFlowLogo, IconDashboard, IconProject, IconWorkflow,
  IconActivity, IconUser, IconSettings, IconPlugins, IconCpu,
  IconLogOut, IconChevronDown, IconChevronRight, IconMenu,
} from '../../icons/SvgIcons';

interface NavItem {
  id: string;
  label: string;
  path: string;
  icon: React.ReactNode;
  children?: NavItem[];
}

const navItems: NavItem[] = [
  { id: 'dashboard', label: 'ダッシュボード', path: '/', icon: <IconDashboard /> },
  { id: 'projects', label: 'プロジェクト', path: '/projects', icon: <IconProject /> },
  { id: 'workflows', label: 'ワークフロー', path: '/workflows', icon: <IconWorkflow /> },
  { id: 'activity', label: 'アクティビティ', path: '/activity', icon: <IconActivity /> },
  { id: 'plugins', label: 'プラグイン', path: '/plugins', icon: <IconPlugins /> },
  { id: 'ai', label: 'AI統合', path: '/ai', icon: <IconCpu /> },
  { id: 'settings', label: '設定', path: '/settings', icon: <IconSettings /> },
];

const Sidebar: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, logout } = useAuthStore();
  const [collapsed, setCollapsed] = useState(false);
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());

  const toggleExpand = (id: string) => {
    const next = new Set(expandedItems);
    if (next.has(id)) next.delete(id);
    else next.add(id);
    setExpandedItems(next);
  };

  const isActive = (path: string) => {
    if (path === '/') return location.pathname === '/';
    return location.pathname.startsWith(path);
  };

  return (
    <aside
      className={`
        glass-sidebar flex flex-col h-screen sticky top-0
        transition-all duration-300 z-40
        ${collapsed ? 'w-[68px]' : 'w-[260px]'}
      `}
    >
      {/* Header */}
      <div className="flex items-center gap-3 px-4 h-16 border-b border-white/5">
        <div className="cursor-pointer shrink-0" onClick={() => setCollapsed(!collapsed)}>
          {collapsed ? <IconMenu size={24} /> : <IconTaskFlowLogo size={32} />}
        </div>
        {!collapsed && (
          <div className="flex-1 min-w-0">
            <h1 className="text-sm font-bold text-gradient">TaskFlow OS</h1>
            <p className="text-[10px] text-white/30">タスク管理システム</p>
          </div>
        )}
      </div>

      {/* Navigation */}
      <nav className="flex-1 overflow-y-auto py-3 px-2">
        {navItems.map((item) => {
          const active = isActive(item.path);
          return (
            <div key={item.id}>
              <button
                onClick={() => {
                  if (item.children) toggleExpand(item.id);
                  else navigate(item.path);
                }}
                className={`
                  w-full flex items-center gap-3 px-3 py-2.5 rounded-xl mb-0.5
                  transition-all duration-200 group relative
                  ${active
                    ? 'bg-gradient-to-r from-emerald-500/15 to-blue-500/10 text-white'
                    : 'text-white/50 hover:text-white/80 hover:bg-white/5'
                  }
                `}
              >
                {/* Active indicator */}
                {active && (
                  <div className="absolute left-0 top-1/2 -translate-y-1/2 w-[3px] h-5 rounded-r-full bg-gradient-to-b from-emerald-400 to-blue-400" />
                )}

                <span className={`shrink-0 ${active ? 'text-emerald-400' : ''}`}>
                  {item.icon}
                </span>

                {!collapsed && (
                  <>
                    <span className="flex-1 text-left text-sm font-medium">{item.label}</span>
                    {item.children && (
                      expandedItems.has(item.id) ? <IconChevronDown size={14} /> : <IconChevronRight size={14} />
                    )}
                  </>
                )}
              </button>

              {/* Children */}
              {item.children && expandedItems.has(item.id) && !collapsed && (
                <div className="ml-8 mb-1">
                  {item.children.map((child) => (
                    <button
                      key={child.id}
                      onClick={() => navigate(child.path)}
                      className={`
                        w-full flex items-center gap-2 px-3 py-2 rounded-lg text-xs
                        ${isActive(child.path) ? 'text-emerald-400 bg-emerald-500/10' : 'text-white/40 hover:text-white/60 hover:bg-white/5'}
                      `}
                    >
                      {child.icon}
                      <span>{child.label}</span>
                    </button>
                  ))}
                </div>
              )}
            </div>
          );
        })}
      </nav>

      {/* User section */}
      <div className="border-t border-white/5 p-3">
        <button
          onClick={() => navigate('/profile')}
          className="w-full flex items-center gap-3 px-2 py-2 rounded-xl hover:bg-white/5 transition-colors"
        >
          {user?.avatar_url ? (
            <img src={user.avatar_url} alt="" className="w-8 h-8 rounded-full border border-white/10" />
          ) : (
            <div className="w-8 h-8 rounded-full bg-gradient-to-br from-emerald-500/30 to-blue-500/30 border border-white/10 flex items-center justify-center">
              <IconUser size={14} color="#10b981" />
            </div>
          )}
          {!collapsed && (
            <div className="flex-1 min-w-0 text-left">
              <p className="text-xs font-medium text-white truncate">{user?.display_name || 'ユーザー'}</p>
              <p className="text-[10px] text-white/30 truncate">{user?.department || user?.position || ''}</p>
            </div>
          )}
        </button>

        {!collapsed && (
          <button
            onClick={logout}
            className="w-full flex items-center gap-2 px-4 py-2 mt-1 rounded-xl text-white/40 hover:text-red-400 hover:bg-red-500/10 transition-colors text-xs"
          >
            <IconLogOut size={14} />
            <span>ログアウト</span>
          </button>
        )}
      </div>
    </aside>
  );
};

export default Sidebar;
